import {
  IssueFieldSource,
  JiraFieldMappers,
  type TemplateMappingSchemaV1,
  type TemplateMappingSchemaV2
} from '@deque/ui-export-jira';
import type { IssueTypeField } from '../getIssueTypeFields';
import { findJiraFieldByKey } from './findJiraFieldByKey';

type InputType = TemplateMappingSchemaV1 | TemplateMappingSchemaV2;
type SelectedFields = {
  axeField: string;
  mappingField: string;
  sourceType: IssueFieldSource;
}[];

const mappers = Object.values(JiraFieldMappers);

class MappingSchema {
  read(
    input: InputType,
    fieldsSchema: IssueTypeField[]
  ): TemplateMappingSchemaV2 {
    const isV2Version = this.isV2(input);

    if (isV2Version) {
      return input;
    }

    const filteredFields = Object.entries(input).reduce<{
      v2Mapping: TemplateMappingSchemaV2['mapping'];
      unmappable: string[];
    }>(
      (result, [key, value]) => {
        const mapperId = this.getMapperId(fieldsSchema, key);

        if (!mapperId) {
          result.unmappable.push(key);
          return result;
        }

        result.v2Mapping[key] = {
          sourceType: IssueFieldSource.Axe,
          source: value,
          mapperId
        };

        return result;
      },
      { v2Mapping: {}, unmappable: [] }
    );
    this.checkForUnprocessedFields(filteredFields.unmappable, fieldsSchema);

    return {
      dmversion: 2,
      dmversioned: true,
      mapping: filteredFields.v2Mapping,
      fields: fieldsSchema
    };
  }

  write(
    fields: SelectedFields,
    fieldsSchema: IssueTypeField[]
  ): TemplateMappingSchemaV2 {
    const filteredFields = fields.reduce<{
      mappable: TemplateMappingSchemaV2['mapping'];
      unmappable: string[];
    }>(
      (result, { axeField, mappingField, sourceType }) => {
        const mapperId = this.getMapperId(fieldsSchema, mappingField);

        if (!mapperId) {
          result.unmappable.push(mappingField);
          return result;
        }

        result.mappable[mappingField] = {
          sourceType,
          source: axeField,
          mapperId
        };

        return result;
      },
      { mappable: {}, unmappable: [] }
    );

    this.checkForUnprocessedFields(filteredFields.unmappable, fieldsSchema);

    return {
      dmversion: 2,
      dmversioned: true,
      mapping: filteredFields.mappable,
      fields: fieldsSchema
    };
  }

  isV2(input: InputType): input is TemplateMappingSchemaV2 {
    return 'dmversion' in input && 'dmversioned' in input;
  }

  private checkForUnprocessedFields(
    fieldKeys: string[],
    fieldsSchema: IssueTypeField[]
  ) {
    const fullyUnmappableFields = fieldKeys.reduce<string[]>(
      (fields, field) => {
        const fieldEntity = findJiraFieldByKey(fieldsSchema, field);

        if (fieldEntity?.required && !fieldEntity?.hasDefaultValue) {
          fields.push(`'${fieldEntity.name}'`);
        }

        return fields;
      },
      []
    );

    if (fullyUnmappableFields.length) {
      throw new Error(
        `The template contains mapped required fields without default value that cannot be mapped: ${fullyUnmappableFields.join(
          ', '
        )}`
      );
    }
  }

  private getMapperId(fieldsSchema: IssueTypeField[], fieldKey: string) {
    const targetFieldSchema = fieldsSchema.find(
      field => field.key === fieldKey || field.name === fieldKey
    );

    if (!targetFieldSchema) {
      return;
    }

    return mappers.find(mapper => mapper.canMapThisField(targetFieldSchema))
      ?.id;
  }
}

export const AxeIssueMappingSchema = new MappingSchema();
